// import React from "react";
// import {
//   MapContainer,
//   TileLayer,
//   Marker,
//   Tooltip,
//   ZoomControl,
// } from "react-leaflet";
// import L from "leaflet";
// // @ts-ignore: allow importing CSS without type declarations
// import "leaflet/dist/leaflet.css";
// import { FullscreenControl } from "react-leaflet-fullscreen";
// // @ts-ignore: allow importing CSS without type declarations
// import "react-leaflet-fullscreen/styles.css";

// interface FleetMarker {
//   id: string;
//   lat: number;
//   lng: number;
// }

// const fleetMarkers: FleetMarker[] = [
//   { id: "#53457", lat: 6.4474, lng: 3.3903 },
//   { id: "#53787", lat: 6.5582, lng: 3.3946 },
//   { id: "#53667", lat: 6.556, lng: 3.391 },
//   { id: "#53267", lat: 6.5304, lng: 3.384 },
//   { id: "#53733", lat: 6.5403, lng: 3.4281 },
// ];

// // Custom dark marker
// const markerIcon = new L.DivIcon({
//   html: `<div style="
//     background-color: #4B5563;
//     border-radius: 50%;
//     width: 16px;
//     height: 16px;
//     border: 2px solid white;
//   "></div>`,
//   className: "",
//   iconSize: [16, 16],
// });

// const FleetrifyDashMap: React.FC = () => {
//   return (
//     <div className="h-[500px] w-full rounded-lg overflow-hidden">
//       <MapContainer
//         center={[6.54, 3.4]}
//         zoom={12}
//         style={{ height: "100%", width: "100%" }}
//         scrollWheelZoom={true}
//         zoomControl={false}
//       >
//         <ZoomControl position="topright" />

//         <TileLayer
//           attribution='&copy; <a href="https://osm.org/copyright">OpenStreetMap</a> contributors'
//           url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
//         />

//         <FullscreenControl forceSeparateButton={true} position="topright" />

//         {fleetMarkers.map((marker) => (
//           <Marker
//             key={marker.id}
//             position={[marker.lat, marker.lng]}
//             icon={markerIcon}
//           >
//             <Tooltip direction="top" offset={[0, -10]} permanent>
//               {marker.id}
//             </Tooltip>
//           </Marker>
//         ))}
//       </MapContainer>
//     </div>
//   );
// };

// export default FleetrifyDashMap;


import React, { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  MapContainer,
  TileLayer,
  Marker,
  Tooltip,
  ZoomControl,
  useMap,
} from "react-leaflet";
import L from "leaflet";
// @ts-ignore
import "leaflet/dist/leaflet.css";
// @ts-ignore
import "leaflet-fullscreen/dist/leaflet.fullscreen.css";
import { selectUser } from "../../../../redux/slices/authSlice";
import { useGetAllFleetsQuery } from "../../../../redux/services/fleetManagement";
import { useDriverLocationUpdates } from "../../../../hooks/useDriverLocationUpdates";

interface DriverPin {
  id: string;
  name: string;
  lat: number | null;
  lng: number | null;
  status: "online" | "offline";
  lastSeenAt: string | null;
}

const parseCoordinate = (value: unknown): number | null => {
  if (value === null || value === undefined) return null;
  const parsed = typeof value === "number" ? value : parseFloat(value as string);
  return Number.isNaN(parsed) ? null : parsed;
};

const createPinIcon = (color: string) =>
  new L.DivIcon({
    html: `<svg width="24" height="32" viewBox="0 0 24 32" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 0C5.383 0 0 5.383 0 12c0 9 12 20 12 20s12-11 12-20c0-6.617-5.383-12-12-12z" fill="${color}" stroke="white" stroke-width="1.5"/>
      <circle cx="12" cy="12" r="4.5" fill="white"/>
    </svg>`,
    className: "",
    iconSize: [24, 32],
    iconAnchor: [12, 32],
  });

const onlineIcon = createPinIcon("#4B5563");
const offlineIcon = createPinIcon("#9CA3AF");

function FullscreenControl({ position = "topright" }: { position?: string }): null {
  const map = useMap();

  useEffect(() => {
    let control: any;
    let cancelled = false;

    // @ts-ignore
    import("leaflet-fullscreen").then(() => {
      if (cancelled) return;
      control = (L.control as any).fullscreen({
        position,
        title: "Enter fullscreen",
        titleCancel: "Exit fullscreen",
        forceSeparateButton: true,
      });
      map.addControl(control);
    });

    return () => {
      cancelled = true;
      if (control) {
        map.removeControl(control);
      }
    };
  }, [map, position]);

  return null;
}

const FleetrifyDashMap: React.FC = () => {
  const user = useSelector(selectUser);
  const companyId = user?.company?.id;

  const { data: fleetsData } = useGetAllFleetsQuery(
    { company_id: companyId },
    { skip: !companyId }
  );
  const liveUpdates = useDriverLocationUpdates(companyId);

  const basePins = useMemo(() => {
    const fleets = fleetsData?.data?.fleets?.data || [];
    const pins = new Map<string, DriverPin>();

    fleets.forEach((fleet: any) => {
      const staffMember = fleet.user?.staff_member;
      if (!staffMember || staffMember.last_seen === null) return;

      pins.set(staffMember.id, {
        id: staffMember.id,
        name: fleet.user?.name || "Driver",
        lat: parseCoordinate(staffMember.latitude),
        lng: parseCoordinate(staffMember.longitude),
        status: staffMember.last_seen === "online" ? "online" : "offline",
        lastSeenAt: staffMember.last_seen_at ?? null,
      });
    });

    return pins;
  }, [fleetsData]);

  const pins = useMemo(() => {
    const merged = new Map(basePins);

    Object.values(liveUpdates).forEach((event) => {
      const existing = merged.get(event.staff_member_id);
      merged.set(event.staff_member_id, {
        id: event.staff_member_id,
        name: existing?.name || "Driver",
        lat: event.latitude ?? existing?.lat ?? null,
        lng: event.longitude ?? existing?.lng ?? null,
        status: event.status,
        lastSeenAt: event.status === "offline" ? event.updated_at : existing?.lastSeenAt ?? null,
      });
    });

    return Array.from(merged.values()).filter((pin) => pin.lat !== null && pin.lng !== null);
  }, [basePins, liveUpdates]);

  return (
    <div className="h-[500px] w-full rounded-lg overflow-hidden pointer-events-auto" style={{ position: "relative", zIndex: 0 }}>
      <MapContainer
        center={[6.54, 3.4]}
        zoom={12}
        style={{ height: "100%", width: "100%" }}
        scrollWheelZoom={true}
        zoomControl={false}
      >
        <ZoomControl position="topright" />
        <TileLayer
          attribution='&copy; <a href="https://osm.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FullscreenControl position="topright" />
        {pins.map((pin) => (
          <Marker
            key={pin.id}
            position={[pin.lat as number, pin.lng as number]}
            icon={pin.status === "online" ? onlineIcon : offlineIcon}
          >
            <Tooltip direction="top" offset={[0, -34]}>
              {pin.name}
              {pin.status === "offline" && (
                <>
                  {" — offline"}
                  {pin.lastSeenAt &&
                    `, last seen ${new Date(pin.lastSeenAt).toLocaleString()}`}
                </>
              )}
            </Tooltip>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
};

export default FleetrifyDashMap;